/**
 * M7 S4: story items (spec §2.8; break-it SL-1, SL-2, EX-1, EX-2). The mission items (the Column Nine recorder,
 * Kell's sealed package, the report core, Settlement file 50-A, the lab notes) are neither wares nor contract cargo.
 *
 * - **The record is the truth:** `career.story.items[] = { id, state, where?, contraband?, pod? }`.
 *   `held` = the player has the original; `copy` = the original left (sold or handed over) and the player kept a
 *   partial copy (× `case.json weights.copyMult`); `withheld` = Hale holds the original and the player keeps a copy;
 *   `sold` = the original left the world and no copy was kept; `lost` = gone. `where` names who has the original
 *   when the player doesn't (a buyer, `hale`); `pod` names the death pod holding the player's original or copy.
 * - **The hold mirror:** every item the player has (`held`, `copy`, `withheld`) and that isn't in a death pod shows
 *   in the hold as one sealed `CargoComponent` tagged `story: true` (`items.json`: id `story:<item>`, no mass, no
 *   value). `syncStoryHold` repairs the hold from the record (on load, after any hold change); the one fact it takes
 *   from the hold is a death pod coming back aboard (the mirror is in the hold again, so `pod` clears).
 * - **Sealed:** no station quotes a story component, or a pod carrying one (`economy/quote.ts`), so nothing sells
 *   them; they leave the player only through a fork (`sellItem`, `copyItem`, `withholdItem`) or `loseItem` (EX-1).
 * - **Out of the world when sold:** a sale emits no command at all, so the original is never NPC cargo (EX-2).
 * - **Never on a contract:** mirrors carry no `contractId`, so `expireContracts`/`dropContractCargo` never touch
 *   them, and mission steps never enter `contracts.active` (S6; SL-1).
 * - **Death:** `applyDeath` moves the mirrors into the death wreck's pod (`markItemsInDeathPod`); the salvage loot
 *   check never empties story components (`space/salvage.ts`, SL-2).
 *
 * Pure: the input career is never changed. Story logic is event-driven; nothing here runs per frame, except
 * `contentsHaveStory`, which is allocation-free so the salvage end stage can call it.
 */
import { z } from 'zod'
import { fail, ok, type CmdResult } from '../career/result'
import type { CargoComponent, CargoContents, Career } from '../career/types'
import { logger } from '../core/logger'
import { COMPONENT_TYPES } from '../data/m2Schemas'
import itemsJson from '../data/story/items.json'
import { storyItemIdSchema } from './schemas'
import { storyOf, withStory } from './state'
import { STORY_ITEM_IDS, type StoryEffect, type StoryItem, type StoryItemId, type StoryResult } from './types'

// ---------------------------------------------------------------------------------------------
// items.json
// ---------------------------------------------------------------------------------------------

const itemDefSchema = z.strictObject({
  type: z.enum(COMPONENT_TYPES),
  massT: z.number().nonnegative(),
  contraband: z.boolean(),
})

export const storyItemsFileSchema = z
  .strictObject({
    comment: z.string(),
    componentIdPrefix: z.string().min(1),
    deathPodPrefix: z.string().min(1),
    items: z.record(storyItemIdSchema, itemDefSchema),
  })
  .refine((f) => STORY_ITEM_IDS.every((id) => f.items[id] !== undefined), { message: 'every story item needs a def' })
export type StoryItemsConfig = z.output<typeof storyItemsFileSchema>

/** Parses an `items.json` (tests pass their own); throws with the path on a bad file. */
export function loadStoryItems(raw: unknown): StoryItemsConfig {
  const result = storyItemsFileSchema.safeParse(raw)
  if (result.success) return result.data
  const issue = result.error.issues[0]
  const where = issue && issue.path.length > 0 ? issue.path.map(String).join('.') : '(root)'
  throw new Error(`story data: items.json: ${where}: ${issue?.message ?? 'invalid'}`)
}

let cached: StoryItemsConfig | undefined

/** The shipped `items.json`, parsed once on first use. */
export function storyItemsConfig(): StoryItemsConfig {
  cached ??= loadStoryItems(itemsJson)
  return cached
}

// ---------------------------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------------------------

/** States in which the player has the original or a copy (and so a hold mirror, unless it is in a death pod). */
const WITH_PLAYER: ReadonlySet<StoryItem['state']> = new Set(['held', 'copy', 'withheld'])

/** The player has the original or a copy (in the hold or in a death pod). */
export function itemWithPlayer(item: StoryItem): boolean {
  return WITH_PLAYER.has(item.state)
}

/** The player has it aboard: its mirror belongs in the hold. */
export function itemAboard(item: StoryItem): boolean {
  return itemWithPlayer(item) && item.pod === undefined
}

export function storyItemOf(career: Career, id: StoryItemId): StoryItem | undefined {
  return storyOf(career).items.find((i) => i.id === id)
}

/** The hold mirror's component id (`story:<item>`). */
export function storyComponentId(id: StoryItemId, cfg: StoryItemsConfig = storyItemsConfig()): string {
  return cfg.componentIdPrefix + id
}

/** The death pod id for the `n`-th death (`death:<n>`). */
export function deathPodId(n: number, cfg: StoryItemsConfig = storyItemsConfig()): string {
  return cfg.deathPodPrefix + String(n)
}

/** A story component: an item's hold mirror. */
export function isStoryComponent(c: Readonly<CargoComponent>): boolean {
  return c.story === true
}

/** Pod contents holding a story component at any depth. Allocation-free (the salvage end stage calls it). */
export function contentsHaveStory(contents: Readonly<CargoContents>): boolean {
  const list = contents.components
  for (let i = 0; i < list.length; i++) {
    const c = list[i]
    if (c.story === true) return true
    if (c.contents !== undefined && contentsHaveStory(c.contents)) return true
  }
  return false
}

/** A story component, or a pod carrying one: no station quotes it (spec §2.8 "sealed"). */
export function componentCarriesStory(c: Readonly<CargoComponent>): boolean {
  return c.story === true || (c.contents !== undefined && contentsHaveStory(c.contents))
}

/** Is a contraband story item aboard (the patrol scan reads it, spec §5)? */
export function holdHasContraband(career: Career): boolean {
  return storyOf(career).items.some((i) => i.contraband === true && itemAboard(i))
}

/** The hold mirror of `item` (sealed, story, no contract; no mass and no value by default). */
export function storyItemMirror(item: StoryItem, cfg: StoryItemsConfig = storyItemsConfig()): CargoComponent {
  const def = cfg.items[item.id]
  const c: CargoComponent = {
    id: storyComponentId(item.id, cfg),
    type: def?.type ?? 'cargoPod',
    condition: 1,
    massT: def?.massT ?? 0,
    value: 0,
    sealed: true,
    story: true,
  }
  if (item.contraband === true) c.contraband = true
  return c
}

/** Story item id of a mirror component id, or null. */
export function storyItemIdOfComponent(componentId: string, cfg: StoryItemsConfig = storyItemsConfig()): StoryItemId | null {
  if (!componentId.startsWith(cfg.componentIdPrefix)) return null
  const id = componentId.slice(cfg.componentIdPrefix.length)
  return (STORY_ITEM_IDS as readonly string[]).includes(id) ? (id as StoryItemId) : null
}

// ---------------------------------------------------------------------------------------------
// The hold mirror
// ---------------------------------------------------------------------------------------------

function mirrorIn(components: readonly CargoComponent[], mirrorId: string): boolean {
  for (const c of components) {
    if (c.id === mirrorId && c.story === true) return true
    if (c.contents !== undefined && mirrorIn(c.contents.components, mirrorId)) return true
  }
  return false
}

/** Drops every story component from a component list (nested pods included); the same array when there is none. */
function withoutStory(components: readonly CargoComponent[], keep: (c: CargoComponent) => boolean): CargoComponent[] {
  let changed = false
  const out: CargoComponent[] = []
  for (const c of components) {
    if (c.story === true && !keep(c)) {
      changed = true
      continue
    }
    if (c.contents !== undefined) {
      const inner = withoutStory(c.contents.components, keep)
      if (inner !== c.contents.components) {
        changed = true
        out.push({ ...c, contents: { ...c.contents, components: inner } })
        continue
      }
    }
    out.push(c)
  }
  return changed ? out : (components as CargoComponent[])
}

function withHoldComponents(career: Career, components: CargoComponent[]): Career {
  if (components === career.cargo.components) return career
  return { ...career, cargo: { ...career.cargo, components } }
}

/**
 * Repairs the hold from the record (spec §2.8: the record is the truth), after a load or any hold change:
 * - an item with `pod` whose mirror is back in the hold (the death pod was recovered): `pod` clears;
 * - every item aboard has exactly one mirror, top-level, matching its record (contraband);
 * - mirrors of items the player no longer has (sold, lost, unknown ids, duplicates) are removed.
 * Mirrors inside an unopened pod in the hold count as aboard and stay there. The same career when nothing changes.
 */
export function syncStoryHold(career: Career, cfg: StoryItemsConfig = storyItemsConfig()): Career {
  const story = storyOf(career)
  const components = career.cargo.components
  // Hold → record: a recovered death pod.
  let items = story.items
  for (let i = 0; i < items.length; i++) {
    const item = items[i]
    if (item.pod === undefined || !itemWithPlayer(item) || !mirrorIn(components, storyComponentId(item.id, cfg))) continue
    if (items === story.items) items = [...items]
    const back = { ...item }
    delete back.pod
    items[i] = back
  }
  // Record → hold.
  const wanted = new Map<string, StoryItem>()
  for (const item of items) if (itemAboard(item)) wanted.set(storyComponentId(item.id, cfg), item)
  const seen = new Set<string>()
  let next = withoutStory(components, (c) => {
    const item = wanted.get(c.id)
    if (item === undefined || seen.has(c.id)) return false
    if (c.sealed !== true || (c.contraband === true) !== (item.contraband === true) || c.contractId !== undefined) return false
    seen.add(c.id)
    return true
  })
  for (const [mirrorId, item] of wanted) {
    if (seen.has(mirrorId)) continue
    if (next === components) next = [...components]
    next.push(storyItemMirror(item, cfg))
  }
  let out = withHoldComponents(career, next)
  if (items !== story.items) out = withStory(out, (s) => ({ ...s, items }))
  return out
}

// ---------------------------------------------------------------------------------------------
// Item changes (forks, pickups, loss). Each returns the repaired hold.
// ---------------------------------------------------------------------------------------------

function result(career: Career, changed: boolean, reason: string): StoryResult<Career> {
  const effects: StoryEffect[] = changed ? [{ type: 'autosave', reason }] : []
  return { career, commands: [], effects }
}

function withItem(career: Career, item: StoryItem, cfg: StoryItemsConfig): Career {
  const next = withStory(career, (s) => {
    const at = s.items.findIndex((i) => i.id === item.id)
    const items = at < 0 ? [...s.items, item] : s.items.map((i, k) => (k === at ? item : i))
    return { ...s, items }
  })
  return syncStoryHold(next, cfg)
}

export interface GrantOptions {
  /** Overrides `items.json contraband` for this item. */
  contraband?: boolean
}

/**
 * The player gets the original `id` (a pickup, a dock interaction, a reward): `held`, its mirror in the hold.
 * Needs no room (mirrors weigh `items.json massT`, 0). An original replaces a copy (`copy`/`withheld` → `held`).
 * A `sold` original left the world and is never granted back (unchanged, logged).
 */
export function grantItem(
  career: Career,
  id: StoryItemId,
  opts: GrantOptions = {},
  cfg: StoryItemsConfig = storyItemsConfig(),
): StoryResult<Career> {
  const prev = storyItemOf(career, id)
  if (prev?.state === 'sold') {
    logger.warn(`story: ${id} was sold and left the world; not granted again`)
    return result(career, false, '')
  }
  if (prev?.state === 'held' && prev.pod === undefined) return result(syncStoryHold(career, cfg), false, '')
  const contraband = opts.contraband ?? prev?.contraband ?? cfg.items[id]?.contraband ?? false
  const item: StoryItem = { id, state: 'held' }
  if (contraband) item.contraband = true
  return result(withItem(career, item, cfg), true, `storyItem:${id}`)
}

function needAboard(career: Career, id: StoryItemId, states: readonly StoryItem['state'][]): StoryItem | ReturnType<typeof fail> {
  const item = storyItemOf(career, id)
  if (item === undefined || !states.includes(item.state)) {
    return fail('notInCargo', 'You don’t have that.', { id })
  }
  if (item.pod !== undefined) return fail('notInCargo', 'It is still in your wreck.', { id, pod: item.pod })
  return item
}

export interface SellOptions {
  /** Keep a partial copy (spec §4: the M1 bounty keeps one, × the copy factor). */
  keepCopy?: boolean
}

/**
 * The fork sale of the original `id` to `buyer` (the M1 bounty: `assetRecovery`): the original leaves the world for
 * good (`sold`, or `copy` with `keepCopy`); `where` records the buyer. It never becomes NPC cargo: no command is
 * emitted. The money is the fork's (S12), not this function's. Refused unless the original is aboard.
 */
export function sellItem(
  career: Career,
  id: StoryItemId,
  buyer: string,
  opts: SellOptions = {},
  cfg: StoryItemsConfig = storyItemsConfig(),
): CmdResult<StoryResult<Career>> {
  const item = needAboard(career, id, ['held'])
  if ('ok' in item) return item
  const next: StoryItem = { ...item, state: opts.keepCopy === true ? 'copy' : 'sold', where: buyer }
  if (next.state === 'sold') delete next.contraband
  return ok(result(withItem(career, next, cfg), true, `storyItem:${id}`))
}

/** The original `id` goes to `where`; the player keeps a partial copy (`copy`). Refused unless the original is aboard. */
export function copyItem(
  career: Career,
  id: StoryItemId,
  where: string,
  cfg: StoryItemsConfig = storyItemsConfig(),
): CmdResult<StoryResult<Career>> {
  return sellItem(career, id, where, { keepCopy: true }, cfg)
}

/** Hale keeps the original (spec §7 `hale_deal`): `withheld`, `where: 'hale'`; the player keeps a copy. */
export function withholdItem(
  career: Career,
  id: StoryItemId,
  cfg: StoryItemsConfig = storyItemsConfig(),
): CmdResult<StoryResult<Career>> {
  const item = needAboard(career, id, ['held'])
  if ('ok' in item) return item
  return ok(result(withItem(career, { ...item, state: 'withheld', where: 'hale' }, cfg), true, `storyItem:${id}`))
}

/** The player's original or copy is gone for good (`lost`): its mirror leaves the hold. Unchanged when not held. */
export function loseItem(career: Career, id: StoryItemId, cfg: StoryItemsConfig = storyItemsConfig()): StoryResult<Career> {
  const item = storyItemOf(career, id)
  if (item === undefined || !itemWithPlayer(item)) return result(career, false, '')
  const lost: StoryItem = { id, state: 'lost' }
  if (item.where !== undefined) lost.where = item.where
  return result(withItem(career, lost, cfg), true, `storyItem:${id}`)
}

/** Sets or clears an item's contraband flag (record and mirror). Unchanged when the item is not on record. */
export function setItemContraband(
  career: Career,
  id: StoryItemId,
  contraband: boolean,
  cfg: StoryItemsConfig = storyItemsConfig(),
): Career {
  const item = storyItemOf(career, id)
  if (item === undefined || (item.contraband === true) === contraband) return career
  const next: StoryItem = { ...item }
  if (contraband) next.contraband = true
  else delete next.contraband
  return withItem(career, next, cfg)
}

/**
 * Death (spec §2.8, SL-2): every item aboard is now in the death wreck's pod `podId` (`death:<n>`). Items already
 * in an earlier pod stay there. The caller moves the mirrors into the pod with the rest of the hold.
 */
export function markItemsInDeathPod(career: Career, podId: string): Career {
  const story = career.story
  if (story === undefined || !story.items.some(itemAboard)) return career
  const items = story.items.map((i) => (itemAboard(i) ? { ...i, pod: podId } : i))
  return { ...career, story: { ...story, items } }
}

/**
 * What a death pod keeps when the salvage loot check finds raiders (spec §2.8, SL-2): only its story components
 * (from any depth, flattened); every ware and every other component is taken. Called once per death wreck, never
 * per frame. A story item never protects the rest of the hold (D-TBD: the loot roll still takes the other cargo).
 */
export function lootedStoryContents(contents: Readonly<CargoContents>, empty: CargoContents): CargoContents {
  const keep: CargoComponent[] = []
  collectStory(contents.components, keep)
  return { wares: empty.wares, components: keep }
}

function collectStory(components: readonly CargoComponent[], out: CargoComponent[]): void {
  for (const c of components) {
    if (c.story === true) out.push(c)
    else if (c.contents !== undefined) collectStory(c.contents.components, out)
  }
}
